import { Component, OnInit, ElementRef, ViewChildren, QueryList, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { MeetingApiService } from '../services/meeting-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { MeetingDetailModalComponent } from '../meeting-detail-modal/meeting-detail-modal.component';
import { MeetingCancelDialogComponent } from '../shared/meeting-cancel-dialog/meeting-cancel-dialog.component';
import { MeetingRescheduleDialogComponent } from '../shared/meeting-reschedule-dialog/meeting-reschedule-dialog.component';
import { isCancelStatus, isMeetingCancelled, isRescheduleStatus } from '../shared/meeting-action.util';
import {
  Meeting,
  MeetingRequest,
  MeetingStatus,
  MeetingType,
  MeetingSearchParams
} from '../models/meeting.model';
import { Contact } from '../../contactos/models/contacto.model';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

type ViewMode = 'status' | 'type';

interface KanbanColumn {
  id: number;
  name: string;
  color: string;
  meetings: Meeting[];
  currentPage: number;
  hasMore: boolean;
  loading: boolean;
  totalElements: number;
}

interface FilterState {
  searchTerm: string;
  idMeetingStatus?: number;
  idMeetingType?: number;
  dateFrom?: string;
  dateTo?: string;
}

@Component({
  selector: 'app-meeting-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    BadgeComponent,
    MeetingDetailModalComponent,
    MeetingCancelDialogComponent,
    MeetingRescheduleDialogComponent
  ],
  templateUrl: './meeting-kanban.component.html',
  styleUrl: './meeting-kanban.component.css'
})
export class MeetingKanbanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('columnScrollContainer') columnScrollContainers!: QueryList<ElementRef>;

  viewMode: ViewMode = 'status';
  columns: KanbanColumn[] = [];
  loading = false;
  error = '';

  // Catálogos
  meetingStatuses: MeetingStatus[] = [];
  meetingTypes: MeetingType[] = [];
  contacts: Contact[] = [];
  loadingContacts = false;

  // Filtro de organizador (responsable)
  filtroOrganizador: 'all' | 'mine' = 'all';

  // Filtros
  filters: FilterState = {
    searchTerm: '',
    idMeetingStatus: undefined,
    idMeetingType: undefined,
    dateFrom: undefined,
    dateTo: undefined
  };
  showFilters = false;

  // Modal de detalle
  showDetailModal = false;
  selectedMeeting: Meeting | null = null;

  // INC 66/67 — modales de cancelar / reprogramar disparados por drag-drop
  showCancelDialog = false;
  showRescheduleDialog = false;
  accionMeeting: Meeting | null = null;
  accionTargetStatusId?: number;
  /** Columnas afectadas por el drop que abrió un modal (para recargar/revertir). */
  private pendingDrop: { targetColumn: KanbanColumn; sourceColumn?: KanbanColumn } | null = null;

  // Intersection Observers para scroll infinito
  private observers: Map<number, IntersectionObserver> = new Map();

  // Constantes
  private readonly PAGE_SIZE = 10;

  constructor(
    private meetingApi: MeetingApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.loadCatalogs();
    this.cargarContactos();
  }

  

  ngAfterViewInit(): void {
    this.setupIntersectionObservers();
    this.columnScrollContainers.changes.subscribe(() => {
      this.setupIntersectionObservers();
    });
  }

  ngOnDestroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }

  private loadCatalogs(): void {
    this.loading = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    Promise.all([
      this.meetingApi.getMeetingStatuses(businessId, true).toPromise(),
      this.meetingApi.getMeetingTypes(businessId, true).toPromise()
    ]).then(([statuses, types]) => {
      this.meetingStatuses = statuses || [];
      this.meetingTypes = types || [];

      this.initializeColumns();
      this.loading = false;
    }).catch(err => {
      this.error = 'Error al cargar catálogos';
      this.loading = false;
      console.error('Error:', err);
    });
  }

   // Método nuevo para cargar contactos
  private cargarContactos(): void {
  const user = this.authService.getCurrentUser();
  const businessId = user?.businessId || 1;
  
  this.loadingContacts = true;
  
  // cargar o mostrar los cantactos
  this.contactoApi.searchContacts({
    businessId,
    searchTerm: '',  
    page: 0,
    size: 100  
  }).subscribe({
    next: (response: any) => {
      this.contacts = response.content || [];
      this.loadingContacts = false;
    },
    error: (err: any) => {
      console.error('Error cargando contactos', err);
      this.loadingContacts = false;
    }
  });
  }
  
  //
  private initializeColumns(): void {
    this.columns = [];

    switch (this.viewMode) {
      case 'status':
        this.buildStatusColumns();
        break;
      case 'type':
        this.buildTypeColumns();
        break;
    }

    // Cargar la primera página de cada columna
    this.columns.forEach(column => {
      this.loadColumnMeetings(column, 0);
    });
  }

  private buildStatusColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-yellow-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-orange-500'
    ];

    // Ordenar por order si existe
    const sortedStatuses = [...this.meetingStatuses].sort((a, b) => 
      (a.order || 0) - (b.order || 0)
    );

    this.columns = sortedStatuses.map((status, index) => ({
      id: status.id,
      name: status.name,
      color: status.color || colors[index % colors.length],
      meetings: [],
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalElements: 0
    }));

    // Agregar columna para reuniones sin estado
    this.columns.push({
      id: 0,
      name: 'Sin Estado',
      color: 'bg-gray-500',
      meetings: [],
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalElements: 0
    });
  }

  private buildTypeColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500'
    ];

    const sortedTypes = [...this.meetingTypes].sort((a, b) => 
      (a.order || 0) - (b.order || 0)
    );

    this.columns = sortedTypes.map((type, index) => ({
      id: type.id,
      name: type.name,
      color: type.color || colors[index % colors.length],
      meetings: [],
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalElements: 0
    }));

    // Agregar columna para reuniones sin tipo
    this.columns.push({
      id: 0,
      name: 'Sin Tipo',
      color: 'bg-gray-500',
      meetings: [],
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalElements: 0
    });
  }

  private loadColumnMeetings(column: KanbanColumn, page: number): void {
    if (column.loading || (!column.hasMore && page > 0)) {
      return;
    }

    column.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Construir parámetros de búsqueda
    const searchParams: MeetingSearchParams = {
      businessId,
      page,
      size: this.PAGE_SIZE
    };

    // Agregar filtro de búsqueda global
    if (this.filters.searchTerm) {
      searchParams.searchTerm = this.filters.searchTerm;
    }

    // Filtro de organizador
    if (this.filtroOrganizador === 'mine') {
      searchParams.organizerId = user?.id;
    }

    // Filtros de fecha
    if (this.filters.dateFrom) {
      searchParams.dateFrom = this.filters.dateFrom;
    }
    if (this.filters.dateTo) {
      searchParams.dateTo = this.filters.dateTo;
    }

    // Agregar el filtro específico de la columna
    switch (this.viewMode) {
      case 'status':
        if (column.id !== 0) {
          searchParams.idMeetingStatus = column.id;
        }
        if (this.filters.idMeetingType !== undefined) {
          searchParams.idMeetingType = this.filters.idMeetingType;
        }
        break;
      case 'type':
        if (column.id !== 0) {
          searchParams.idMeetingType = column.id;
        }
        if (this.filters.idMeetingStatus !== undefined) {
          searchParams.idMeetingStatus = this.filters.idMeetingStatus;
        }
        break;
    }

    this.meetingApi.searchMeetings(searchParams).subscribe({
      next: (response) => {
        // Filtrar reuniones para asegurar que solo pertenezcan a esta columna
        let filteredMeetings = response.content;

        if (column.id === 0) {
          switch (this.viewMode) {
            case 'status':
              filteredMeetings = response.content.filter(m => 
                !m.idMeetingStatus || m.idMeetingStatus === 0
              );
              break;
            case 'type':
              filteredMeetings = response.content.filter(m => 
                !m.idMeetingType || m.idMeetingType === 0
              );
              break;
          }
        } else {
          switch (this.viewMode) {
            case 'status':
              filteredMeetings = response.content.filter(m => 
                m.idMeetingStatus === column.id
              );
              break;
            case 'type':
              filteredMeetings = response.content.filter(m => 
                m.idMeetingType === column.id
              );
              break;
          }
        }

        column.currentPage = page;
        column.hasMore = !response.last;
        column.totalElements = response.totalElements;

        if (page === 0) {
          column.meetings = filteredMeetings;
        } else {
          column.meetings = [...column.meetings, ...filteredMeetings];
        }
        column.loading = false;
      },
      error: (err) => {
        console.error('Error loading column meetings:', err);
        column.loading = false;
        column.hasMore = false;
      }
    });
  }

  private setupIntersectionObservers(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();

    this.columnScrollContainers.forEach((containerRef, index) => {
      const column = this.columns[index];
      if (!column) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && column.hasMore && !column.loading) {
              this.loadColumnMeetings(column, column.currentPage + 1);
            }
          });
        },
        {
          root: containerRef.nativeElement,
          rootMargin: '0px',
          threshold: 0.1
        }
      );

      const sentinel = containerRef.nativeElement.querySelector('.scroll-sentinel');
      if (sentinel) {
        observer.observe(sentinel);
        this.observers.set(column.id, observer);
      }
    });
  }

  changeViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.initializeColumns();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    // Resetear columnas y recargar desde página 0
    this.columns.forEach(column => {
      column.meetings = [];
      column.currentPage = 0;
      column.hasMore = true;
      column.loading = false;
      this.loadColumnMeetings(column, 0);
    });
  }

  setOrganizadorFilter(value: 'all' | 'mine'): void {
    this.filtroOrganizador = value;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filtroOrganizador = 'all';
    this.filters = {
      searchTerm: '',
      idMeetingStatus: undefined,
      idMeetingType: undefined,
      dateFrom: undefined,
      dateTo: undefined
    };
    this.applyFilters();
  }

  get availableFilterFields(): { status: boolean; type: boolean } {
    return {
      status: this.viewMode !== 'status',
      type: this.viewMode !== 'type'
    };
  }

  drop(event: CdkDragDrop<Meeting[]>, targetColumn: KanbanColumn): void {
    const meeting = event.previousContainer.data[event.previousIndex];

    if (event.previousContainer === event.container) {
      // Reordenar dentro de la misma columna
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Encontrar la columna de origen ANTES de mover la reunión
      const sourceColumn = this.columns.find(col => col.meetings === event.previousContainer.data);

      // Mover a otra columna
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      // Actualizar contadores inmediatamente
      if (sourceColumn) {
        sourceColumn.totalElements = Math.max(0, sourceColumn.totalElements - 1);
      }
      targetColumn.totalElements = targetColumn.totalElements + 1;

      // INC 66/67 — en vista por estado, soltar en una columna de CANCELACIÓN o
      // REPROGRAMACIÓN no cambia el estado en silencio: abre el modal correspondiente.
      if (this.viewMode === 'status' && targetColumn.id !== 0) {
        const targetStatus = this.meetingStatuses.find(s => s.id === targetColumn.id);
        if (isCancelStatus(targetStatus)) {
          this.abrirDropDialog('cancel', meeting, targetColumn, sourceColumn);
          return;
        }
        if (isRescheduleStatus(targetStatus)) {
          this.abrirDropDialog('reschedule', meeting, targetColumn, sourceColumn);
          return;
        }
      }

      // Actualizar la reunión en el backend
      this.updateMeetingField(meeting, targetColumn, sourceColumn);
    }
  }

  // ─── INC 66/67: modales disparados por el drop ─────────────────
  private abrirDropDialog(
    kind: 'cancel' | 'reschedule',
    meeting: Meeting,
    targetColumn: KanbanColumn,
    sourceColumn?: KanbanColumn
  ): void {
    this.accionMeeting = meeting;
    this.accionTargetStatusId = targetColumn.id;
    this.pendingDrop = { targetColumn, sourceColumn };
    if (kind === 'cancel') {
      this.showCancelDialog = true;
    } else {
      this.showRescheduleDialog = true;
    }
  }

  /** El usuario confirmó: la reunión ya quedó en el estado destino → recargar columnas. */
  onDropAccionConfirmada(): void {
    this.recargarDrop();
  }

  /** El usuario canceló el modal: el backend no cambió → recargar devuelve la tarjeta a origen. */
  onDropAccionCancelada(): void {
    this.recargarDrop();
  }

  private recargarDrop(): void {
    this.showCancelDialog = false;
    this.showRescheduleDialog = false;
    this.accionMeeting = null;
    this.accionTargetStatusId = undefined;
    const pd = this.pendingDrop;
    this.pendingDrop = null;
    if (!pd) return;
    if (pd.sourceColumn && pd.sourceColumn !== pd.targetColumn) {
      this.reloadColumn(pd.sourceColumn);
    }
    this.reloadColumn(pd.targetColumn);
  }

  private updateMeetingField(meeting: Meeting, targetColumn: KanbanColumn, sourceColumn?: KanbanColumn): void {
    if (!meeting.id) return;

    const reloadBoth = () => {
      if (sourceColumn && sourceColumn !== targetColumn) this.reloadColumn(sourceColumn);
      this.reloadColumn(targetColumn);
    };
    const reloadAll = () => this.columns.forEach(col => this.reloadColumn(col));

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Construir objeto de actualización — debe incluir TODOS los campos:
    // el PUT reemplaza el registro completo y omitir campos B13/booking
    // (timezone, video, pago, idBookingLink...) los borraría (INC 4)
    const updatedMeeting: MeetingRequest = {
      title: meeting.title,
      description: meeting.description,
      datetimeStart: meeting.datetimeStart,
      datetimeEnd: meeting.datetimeEnd,
      location: meeting.location,
      meetingUrl: meeting.meetingUrl,
      idMeetingType: meeting.idMeetingType,
      idMeetingStatus: meeting.idMeetingStatus,
      dealId: meeting.dealId,
      companyId: meeting.companyId,
      contactId: meeting.contactId,
      organizerId: meeting.organizerId,
      businessId: meeting.businessId,
      notes: meeting.notes,
      outcome: meeting.outcome,
      cancellationReasonText: meeting.cancellationReasonText,
      idCancellationReason: meeting.idCancellationReason,
      source: meeting.source,
      isNoShow: meeting.isNoShow,
      noShowDetectedAt: meeting.noShowDetectedAt,
      paymentStatus: meeting.paymentStatus,
      paymentAmount: meeting.paymentAmount,
      paymentIntentId: meeting.paymentIntentId,
      videoProvider: meeting.videoProvider,
      videoJoinUrl: meeting.videoJoinUrl,
      inviteeTimezone: meeting.inviteeTimezone,
      gdprConsentGiven: meeting.gdprConsentGiven,
      gdprConsentDatetime: meeting.gdprConsentDatetime,
      userAssignedByRoutingId: meeting.userAssignedByRoutingId,
      routingRuleAppliedId: meeting.routingRuleAppliedId,
      idBookingLink: meeting.idBookingLink,
      timezone: meeting.timezone,
      preQualificationData: meeting.preQualificationData,
      externalEventId: meeting.externalEventId
    };

    switch (this.viewMode) {
      case 'status':
        updatedMeeting.idMeetingStatus = targetColumn.id === 0 ? undefined : targetColumn.id;
        break;
      case 'type':
        updatedMeeting.idMeetingType = targetColumn.id === 0 ? undefined : targetColumn.id;
        break;
    }

    this.meetingApi.updateMeeting(meeting.id, businessId, updatedMeeting).subscribe({
      next: () => reloadBoth(),
      error: (err) => {
        console.error('Error al actualizar reunión:', err);
        const message = err.error?.message
          || 'Error al actualizar la reunión. Por favor, recargue la página.';
        this.confirmDialog.alert({ title: 'No se pudo cambiar el estado', message, variant: 'danger' });
        reloadAll();
      }
    });
  }

  private reloadColumn(column: KanbanColumn): void {
    column.meetings = [];
    column.currentPage = 0;
    column.hasMore = true;
    this.loadColumnMeetings(column, 0);
  }

  getColumnIds(): string[] {
    return this.columns.map(col => `col-${col.id}`);
  }

  nuevaReunion(): void {
    this.router.navigate(['/meeting/crear']);
  }

  vistaLista(): void {
    this.router.navigate(['/meeting']);
  }

  editarReunion(meeting: Meeting, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/meeting/editar', meeting.id]);
  }

  async eliminarReunion(meeting: Meeting, column: KanbanColumn, event: Event): Promise<void> {
    event.stopPropagation();

    if (!meeting.id) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar reunión',
      message: `¿Está seguro de eliminar la reunión "${meeting.title}"?`
    });
    if (ok) {
      const user = this.authService.getCurrentUser();
      const businessId = user?.businessId || 1;

      this.meetingApi.deleteMeeting(meeting.id, businessId).subscribe({
        next: () => {
          // Remover de la columna
          const index = column.meetings.findIndex(m => m.id === meeting.id);
          if (index !== -1) {
            column.meetings.splice(index, 1);
            column.totalElements = Math.max(0, column.totalElements - 1);
          }
          this.toast.deleted('Reunión');
        },
        error: (err) => {
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la reunión' });
        }
      });
    }
  }

  /** INC 80: cancelada según el criterio único (motivo o estado "cancel…"). */
  estaCancelada(meeting: Meeting): boolean {
    return isMeetingCancelled(meeting, this.meetingStatuses);
  }

  getStatusName(id?: number): string {
    if (!id) return 'Sin estado';
    const status = this.meetingStatuses.find(s => s.id === id);
    return status?.name || '—';
  }

  getTypeName(id?: number): string {
    if (!id) return 'Sin tipo';
    const type = this.meetingTypes.find(t => t.id === id);
    return type?.name || '—';
  }

  getContactName(contactId: number | null | undefined): string {
    if (!contactId) return 'Sin contacto';
    
    const contact = this.contacts.find(c => c.id === contactId);
    if (!contact) return `ID: ${contactId}`;
    
    // Formato: "Nombre Apellido"
    return `${contact.fullname || ''}${contact.lastname ? ' ' + contact.lastname : ''}`.trim();
    }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusColor(id?: number): string {
    if (!id) return 'gray';
    const status = this.meetingStatuses.find(s => s.id === id);
    return status?.color || 'gray';
}

getTypeColor(id?: number): string {
    if (!id) return 'gray';
    const type = this.meetingTypes.find(t => t.id === id);
    return type?.color || 'gray';
}

  verDetalle(meeting: Meeting): void {
    this.selectedMeeting = meeting;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedMeeting = null;
  }
}